// services/podcastModerationService.ts
// Client-side service for podcast moderation using Cloud Functions

import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    limit as firestoreLimit,
    Unsubscribe,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { callWithIdempotency } from '../firebase/callable';

/**
 * TypeScript Types
 */
export type ReviewStatus = 'draft' | 'pending' | 'approved' | 'rejected' | 'published' | 'removed' | 'flagged';

export type RejectionCategory =
    | 'inappropriate_content'
    | 'audio_quality'
    | 'guideline_violation'
    | 'copyright_concern'
    | 'spam'
    | 'other';

export interface PodcastSubmission {
    id: string;
    title: string;
    description: string;
    audioURL: string;
    coverArtURL?: string;
    creatorProfileID: string;
    creatorName: string;
    parentAppleID: string;
    duration: number;
    category: string;
    reviewStatus: ReviewStatus;
    submittedAt: Date | null;
    reviewedAt: Date | null;
    reviewedBy: string | null;
    rejectionReason: string | null;
    rejectionCategory: RejectionCategory | null;
    isPublished: boolean;
    publishedAt: Date | null;
    sharedToBarkBoard: boolean;
}

export interface ModerationStats {
    totalPending: number;
    totalReviewedToday: number;
    totalReviewedThisWeek: number;
    approvalRate: number;
    averageReviewTimeMinutes: number;
}

interface PodcastCallableResponse {
    success: boolean;
    message: string;
    episodeId: string;
}

interface ApprovePodcastRequest {
    episodeId: string;
    autoPublish: boolean;
    autoShareToBarkBoard: boolean;
}

interface RejectPodcastRequest {
    episodeId: string;
    reason: string;
    category: RejectionCategory;
}

interface PublishPodcastRequest {
    episodeId: string;
    shareToBarkBoard: boolean;
    featured: boolean;
}

interface RemovePodcastRequest {
    episodeId: string;
    reason: string;
}

/**
 * Approve a podcast episode
 */
export async function approvePodcast(
    episodeId: string,
    options?: {
        autoPublish?: boolean;
        autoShareToBarkBoard?: boolean;
    }
): Promise<PodcastCallableResponse> {
    return callWithIdempotency<ApprovePodcastRequest, PodcastCallableResponse>(
        'approvePodcast',
        {
            episodeId,
            autoPublish: options?.autoPublish || false,
            autoShareToBarkBoard: options?.autoShareToBarkBoard || false,
        }
    );
}

/**
 * Reject a podcast episode with feedback
 */
export async function rejectPodcast(
    episodeId: string,
    reason: string,
    category: RejectionCategory
): Promise<PodcastCallableResponse> {
    if (reason.length < 10) {
        throw new Error('Rejection reason must be at least 10 characters');
    }

    return callWithIdempotency<RejectPodcastRequest, PodcastCallableResponse>(
        'rejectPodcast',
        { episodeId, reason, category }
    );
}

/**
 * Publish a podcast episode to the public feed
 */
export async function publishPodcast(
    episodeId: string,
    options?: {
        shareToBarkBoard?: boolean;
        featured?: boolean;
    }
): Promise<PodcastCallableResponse> {
    return callWithIdempotency<PublishPodcastRequest, PodcastCallableResponse>(
        'publishPodcast',
        {
            episodeId,
            shareToBarkBoard: options?.shareToBarkBoard || false,
            featured: options?.featured || false,
        }
    );
}

/**
 * Remove a podcast episode for severe violations
 */
export async function removePodcast(
    episodeId: string,
    reason: string
): Promise<PodcastCallableResponse> {
    if (reason.length < 10) {
        throw new Error('Removal reason must be at least 10 characters');
    }

    return callWithIdempotency<RemovePodcastRequest, PodcastCallableResponse>(
        'removePodcast',
        { episodeId, reason }
    );
}

/**
 * Subscribe to pending podcasts in real-time
 */
export function subscribeToPendingPodcasts(
    filters: {
        status?: ReviewStatus;
        limit?: number;
    },
    onUpdate: (podcasts: PodcastSubmission[]) => void,
    onError?: (error: Error) => void
): Unsubscribe {
    let q = query(
        collection(db, 'soundsLab', 'projects', 'items'),
        where('reviewStatus', '==', filters.status || 'pending'),
        where('isDraft', '==', false),
        orderBy('submittedForReviewAt', 'desc')
    );

    if (filters.limit) {
        q = query(q, firestoreLimit(filters.limit));
    }

    return onSnapshot(
        q,
        (snapshot) => {
            const podcasts: PodcastSubmission[] = snapshot.docs.map((doc) => {
                const data = doc.data();
                return {
                    id: doc.id,
                    title: data.title || 'Untitled',
                    description: data.description || '',
                    audioURL: data.finalAudioURL || '',
                    coverArtURL: data.coverArtURL || undefined,
                    creatorProfileID: data.creatorProfileID || '',
                    creatorName: data.creatorName || 'Unknown',
                    parentAppleID: data.parentAppleID || '',
                    duration: data.duration || 0,
                    category: data.category || 'Other',
                    reviewStatus: data.reviewStatus || 'draft',
                    submittedAt: data.submittedForReviewAt?.toDate() || null,
                    reviewedAt: data.reviewedAt?.toDate() || null,
                    reviewedBy: data.reviewedBy || null,
                    rejectionReason: data.rejectionReason || null,
                    rejectionCategory: data.rejectionCategory || null,
                    isPublished: data.isPublished || false,
                    publishedAt: data.publishedAt?.toDate() || null,
                    sharedToBarkBoard: data.sharedToBarkBoard || false
                };
            });
            onUpdate(podcasts);
        },
        (error) => {
            console.error('Error in podcast subscription:', error);
            if (onError) {
                onError(error);
            }
        }
    );
}

/**
 * Helper Functions for UI Display
 */

export function formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function formatRelativeTime(date: Date | null): string {
    if (!date) return 'N/A';
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
}

export function formatReviewStatus(status: ReviewStatus): string {
    const statusMap: Record<ReviewStatus, string> = {
        draft: 'Draft',
        pending: 'Pending Review',
        approved: 'Approved',
        rejected: 'Rejected',
        published: 'Published',
        removed: 'Removed',
        flagged: 'Flagged'
    };
    return statusMap[status] || status;
}

export function getStatusColor(status: ReviewStatus): string {
    const colorMap: Record<ReviewStatus, string> = {
        draft: 'gray',
        pending: 'yellow',
        approved: 'green',
        rejected: 'red',
        published: 'blue',
        removed: 'red',
        flagged: 'orange'
    };
    return colorMap[status] || 'gray';
}

export function formatRejectionCategory(category: RejectionCategory): string {
    const categoryMap: Record<RejectionCategory, string> = {
        inappropriate_content: 'Inappropriate Content',
        audio_quality: 'Audio Quality Issues',
        guideline_violation: 'Guideline Violation',
        copyright_concern: 'Copyright Concern',
        spam: 'Spam',
        other: 'Other'
    };
    return categoryMap[category] || category;
}

/**
 * Service object for convenient exports
 */
export const PodcastModerationService = {
    approvePodcast,
    rejectPodcast,
    publishPodcast,
    removePodcast,
    subscribeToPendingPodcasts
};

export default PodcastModerationService;
